import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { CompoundItemModalConfig } from '@front-platform/content-builder';
import { compoundItemModalResolveConfig } from '@front-platform/content-builder';
import { Observable } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { Component, input, OnInit, computed } from '@angular/core';

import { ShopKeys } from '@config/keys/shop.keys';
import { ProductKeys } from '@config/keys/product.keys';

import { IAdditionGroup } from '@core/models/shop.model';
import { DoodProductModel } from '@core/models/product.model';

import { ProductTypeValues } from '@config/values/product.values';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { ShopStoreRefiner } from '@common/refiners/shop.refiner';

import { ModalsService } from '@core/services/modals/modals.service';
import { ProductService } from '@core/services/product/product.service';

import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { ContentStoreSelector } from '@common/selectors/content.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { DeclinableStoreSelector } from '@common/selectors/declinable.selector';
import { DeclinableStoreDispatcher } from '@common/dispatchers/declinable.dispatcher';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { CompoundItemPanelAtomComponent } from '@shared/atoms/compound-item-panel-atom/compound-item-panel-atom.component';
import { AsyncPipe } from '@angular/common';
export interface CompoundItemModalRuntimeConfig {}

export type CompoundItemModalConfigInput = Partial<
  Omit<CompoundItemModalConfig, keyof CompoundItemModalRuntimeConfig> &
    CompoundItemModalRuntimeConfig
>;
export type CompoundItemModalResolvedConfig = Omit<
  CompoundItemModalConfig,
  keyof CompoundItemModalRuntimeConfig
> &
  Partial<CompoundItemModalRuntimeConfig>;
export interface CompoundItemModalContract extends ConfigComponentContract<
  CompoundItemModalConfigInput,
  CompoundItemModalResolvedConfig
> {}

@Component({
  selector: 'app-compound-item-modal',
  templateUrl: './compound-item-modal.component.html',
  imports: [CompoundItemPanelAtomComponent, AsyncPipe],
  host: {
    'data-testid': 'compound-product-container',
  },
})
export class CompoundItemModalComponent
  extends DestroyerBase
  implements OnInit, CompoundItemModalContract
{
  readonly config = input<CompoundItemModalConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    CompoundItemModalConfig,
    CompoundItemModalRuntimeConfig
  >(compoundItemModalResolveConfig, () => ({ config: this.config() }));
  static handle = 'compound-item-modal';

  headingColor = computed(
    () => this.resolvedConfig()['heading_color'] as unknown as IContentBuilderFieldColor,
  );
  descriptionColor = computed(
    () => this.resolvedConfig()['description_color'] as unknown as IContentBuilderFieldColor,
  );
  inactiveStateColor = computed(
    () => this.resolvedConfig()['inactive_state_color'] as unknown as IContentBuilderFieldColor,
  );
  priceColor = computed(
    () => this.resolvedConfig()['price_color'] as unknown as IContentBuilderFieldColor,
  );
  backgroundColor = computed(
    () => this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor,
  );

  hideAdditionGroupDetails = computed(
    () => this.resolvedConfig()['hide_addition_group_details'] as unknown as boolean,
  );

  index$: Observable<number> = this.modalSelector
    .selectModal(CompoundItemModalComponent.handle)
    .pipe(map(el => (el?.index ? el.index : 4)));

  compoundActiveItem$ = this.declinableSelector.selectActive$;

  isMobileScreen$ = this.settingsSelector.selectDeviceIsMobileScreen;

  parentIdOfAdditions!: string;

  additions: DoodProductModel[] = [];

  additionGroup?: IAdditionGroup;

  groupId!: string;

  shopProducts!: DoodProductModel[];

  shopAdditions!: DoodProductModel[];

  constructor(
    private shopRefiner: ShopStoreRefiner,
    private shopSelector: ShopStoreSelector,
    private modalSelector: ModalStoreSelector,
    private contentSelector: ContentStoreSelector,
    private readonly modalsService: ModalsService,
    private settingsSelector: SettingsStoreSelector,
    private readonly productService: ProductService,
    private declinableSelector: DeclinableStoreSelector,
    private declinableDispatcher: DeclinableStoreDispatcher,
  ) {
    super();
  }

  ngOnInit(): void {
    const modal = this.contentSelector.productModal;
    if (modal && modal.shopId) {
      this.shopSelector.selectShop(modal.shopId).subscribe(shop => {
        if (shop?.[ShopKeys.Products]) {
          this.shopProducts = shop[ShopKeys.Products];
          this.shopAdditions = shop[ShopKeys.Products].filter(
            product => product[ProductKeys.Type] === ProductTypeValues.Addition,
          );
        }
      });

      this.shopRefiner.selectActiveShopProducts
        .pipe(
          take(1),
          map(products => {
            if (products) {
              this.shopProducts = products;
              this.shopAdditions = products.filter(
                product => product[ProductKeys.Type] === ProductTypeValues.Addition,
              );
            }
          }),
        )
        .subscribe();
    }
  }

  openAdditionsPanel(productInformation: { productId: string; groupId: string }): void {
    this.declinableSelector.selectActive$
      .pipe(
        take(1),
        map(item => item?.products.find(el => el.id === productInformation.productId)),
        map(product => {
          let group = product?.additions_group_informations?.find(
            group => group.id === productInformation.groupId,
          );
          this.additions = [];

          if (group != undefined) {
            product?.additions.products.forEach(productId => {
              let found = group.items.find(item => item.id === productId);
              if (found != undefined) {
                this.additions.push(found);
              }
            });
          }
          return group;
        }),
        tap(group => (this.additionGroup = group)),
      )
      .subscribe();
    this.groupId = productInformation.groupId;
    this.parentIdOfAdditions = productInformation.productId;
    /*
    this.compoundItemQuery
      .selectActive()
      .pipe(
        take(1),
        map(
          item =>
            item?.products.find(el => el.id === productInformation.productId)
              ?.additions_group_informations,
        ),
        map(groups => {
          this.additionGroup = groups?.find(group => group.id === productInformation.groupId);
          this.additions = this.additionGroup?.items as DoodProductModel[];
        }),
      )
      .subscribe();
    */
    this.groupId = productInformation.groupId;
    this.parentIdOfAdditions = productInformation.productId;
  }

  onUpdateProducts(
    products: { products: string[]; parent: string; groupId?: string },
    storeId: string,
  ): void {
    this.productService.updateSelectedCompoundProduct(products.products, storeId);
  }

  onUpdateAdditions(
    products: { products: string[]; parent: string; groupId?: string },
    storeId: string,
  ): void {
    this.productService.updateSelectedAdditionsCompoundProduct({
      products: products.products,
      storeId,
      productId: products.parent,
      shopAdditions: this.shopAdditions,
      groupId: products?.groupId ?? '',
    });
  }

  onValidItem(storeId: string): void {
    const _active = this.declinableSelector.selectActive();
    if (_active) {
      this.declinableDispatcher.mapOne({
        id: _active.store_id,
        map: _declinable => ({ ..._declinable, isVisited: true }),
      });
    }
    // this.compoundItemStore.updateActive({ isVisited: true });

    this.declinableSelector.selectDeclinables$.pipe(take(1)).subscribe(_declinables => {
      const currentIndex = _declinables.findIndex(store => store.store_id === storeId);
      const nextStore = _declinables[(currentIndex + 1) % _declinables.length];
      if (nextStore && !nextStore?.isVisited) {
        // Just change the active item, don't close/reopen the modal
        // This prevents the flash of the parent modal
        this.declinableDispatcher.setActive(nextStore.store_id);
      } else {
        // All items visited, close the modal
        this.modalsService.close(CompoundItemModalComponent.handle);
      }
    });
    /*
    const compoundStores: DoodDeclinableModel[] = this.compoundItemQuery.getAll();
    const currentIndex = compoundStores.findIndex(store => store.store_id === storeId);
    const nextStore = compoundStores[(currentIndex + 1) % compoundStores.length];
    if (nextStore && !nextStore?.isVisited) {
      this.compoundItemStore.setActive(nextStore.store_id);
      this.modalsService.open(CompoundItemModalComponent.handle);
    }
    */
  }

  onCloseModal(): void {
    this.modalsService.close(CompoundItemModalComponent.handle);
  }

  onCloseAdditions(): void {
    this.additions = [];
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(CompoundItemModalComponent, { 'keyAliases': {} });
